import { useId } from "react";
import type { InputHTMLAttributes } from "react";

import { cx } from "../lib/cx";
import { FieldShell, controlClass } from "./FieldShell";

export interface ClayInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "onChange" | "value" | "className"> {
  label: string;
  value: string;
  onValueChange: (value: string) => void;
  error?: string | null;
  hint?: string;
  className?: string;
  /** Hides the label visually while keeping it for assistive tech. */
  hideLabel?: boolean;
}

export function ClayInput({
  label,
  value,
  onValueChange,
  error,
  hint,
  className,
  hideLabel = false,
  id,
  required,
  ...rest
}: ClayInputProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  const control = (
    <input
      {...rest}
      id={inputId}
      value={value}
      required={required}
      aria-invalid={error ? true : undefined}
      aria-describedby={error ? `${inputId}-error` : undefined}
      onChange={(event) => onValueChange(event.target.value)}
      className={cx(controlClass, error && "shadow-[0_0_0_2px_rgba(244,63,94,0.45)]", hideLabel && className)}
    />
  );

  if (hideLabel) {
    return (
      <div className="flex flex-col gap-2">
        <label htmlFor={inputId} className="sr-only">
          {label}
        </label>
        {control}
      </div>
    );
  }

  return (
    <FieldShell
      id={inputId}
      label={label}
      required={required}
      error={error}
      hint={hint}
      className={className}
    >
      {control}
    </FieldShell>
  );
}
